"use client"

import { useState } from "react"
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
  Snackbar,
  Alert,
} from "@mui/material"
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material"
import PostCard from "./PostCard"
import EditPostDialog from "./EditPostDialog"
import NewPostDialog from "./NewPostDialog"
import { useLocation } from "react-router-dom"
import { mapStateToProps, mapDispachToProps } from "../../../../store/MapToProps"
import { connect } from "react-redux"
import { getData } from "../../../../utils/helpers"

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(3),
  "& .MuiTab-root": {
    textTransform: "none",
    minWidth: 120,
    fontWeight: 500,
  },
}))

const categories = [
  { id: "companyPresentations", label: "Company Presentations" },
  { id: "jobAdvertisements", label: "Job Advertisements" },
  { id: "flyers", label: "Flyers" },
  { id: "menus", label: "Menus" },
  { id: "infos", label: "Information" },
  { id: "events", label: "Events" },
  { id: "advertisingVideos", label: "Advertising Videos" },
  { id: "PRFeeds", label: "PR Feeds" },
]

const ContentTabs = ({ provider, ...props }) => {
  const location = useLocation()
  const [activeTab, setActiveTab] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPost, setSelectedPost] = useState(null)
  const [editingPost, setEditingPost] = useState(null)
  const [isNewPostDialogOpen, setIsNewPostDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" })

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
  }

  const handleDeleteClick = (post) => {
    setSelectedPost(post)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/posts/delete_post/${selectedPost._id}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
        },
      })

      if (response.ok) {
        setSnackbar({ open: true, message: "Post deleted successfully", severity: "success" })
        if (location.state?.id) {
          getData(props, location.state.pageTitle, location.state.id)
        }
      } else {
        throw new Error("Failed to delete post")
      }
    } catch (error) {
      console.error("Error deleting post:", error)
      setSnackbar({ open: true, message: "Failed to delete post", severity: "error" })
    } finally {
      setDeleteDialogOpen(false)
      setSelectedPost(null)
    }
  }

  const handleEditPost = (post) => {
    setEditingPost(post)
  }

  const handleSaveEdit = async () => {
    if (location.state?.id) {
      getData(props, location.state.pageTitle, location.state.id)
    }
    setEditingPost(null)
  }

  const handleNewPost = () => {
    setIsNewPostDialogOpen(true)
  }

  const handleSaveNewPost = async () => {
    if (location.state?.id) {
      getData(props, location.state.pageTitle, location.state.id)
    }
    setIsNewPostDialogOpen(false)
  }

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  return (
    <>
      <StyledTabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="content categories"
      >
        {categories.map((category) => (
          <Tab key={category.id} label={category.label} />
        ))}
      </StyledTabs>

      {categories.map((category, index) => (
        <Box key={category.id} role="tabpanel" hidden={activeTab !== index} id={`tabpanel-${index}`}>
          {activeTab === index && (
            <>
              <Box sx={{ mb: 3 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleNewPost} size="large">
                  Add New {category.label.slice(0, -1)}
                </Button>
              </Box>

              <Grid container spacing={3}>
                {provider.posts?.[category.id]?.map((post) => (
                  <Grid item xs={12} sm={6} md={4} key={post._id}>
                    <Box sx={{ position: "relative" }}>
                      <PostCard post={post} />
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          display: "flex",
                          gap: 1,
                          backgroundColor: "rgba(255, 255, 255, 0.9)",
                          borderRadius: 1,
                          p: 0.5,
                        }}
                      >
                        <IconButton size="small" sx={{ bgcolor: "background.paper" }}>
                          <EditIcon fontSize="small" onClick={() => handleEditPost(post)} />
                        </IconButton>
                        <IconButton
                          size="small"
                          sx={{ bgcolor: "background.paper" }}
                          onClick={() => handleDeleteClick(post)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      ))}

      {editingPost && (
        <EditPostDialog
          open={Boolean(editingPost)}
          onClose={() => setEditingPost(null)}
          post={editingPost}
          onSave={handleSaveEdit}
          category={categories[activeTab].label.slice(0, -1)}
        />
      )}

      <NewPostDialog
        open={isNewPostDialogOpen}
        onClose={() => setIsNewPostDialogOpen(false)}
        onSave={handleSaveNewPost}
        category={categories[activeTab].id}
        providerId={provider._id}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default connect(mapStateToProps, mapDispachToProps)(ContentTabs)

